import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import type { ExternalFilters, ExternalFiltersConfig, GridScope } from '../types'
import {
  removeStorageItem,
  writeStorageItem,
} from '../storage'
import {
  areExternalFiltersEqual,
  getExternalFiltersStorageKey,
  loadExternalFiltersFromStorage,
  parseExternalFilters,
  parseExternalFiltersString,
  pruneExternalFilters,
} from './externalFiltersStorage'

export { parseExternalFilters, parseExternalFiltersString }

type UseExternalFiltersPersistenceParams<Row, Ctx> = {
  config?: ExternalFiltersConfig<Row, Ctx>
  scope: GridScope
}

type UseExternalFiltersPersistenceResult = {
  externalFiltersSelected: ExternalFilters
  setExternalFiltersSelected: Dispatch<SetStateAction<ExternalFilters>>
  externalFiltersStorageKey: string
  resetExternalFilters: () => void
}

export const useExternalFiltersPersistence = <Row, Ctx>({
  config,
  scope,
}: UseExternalFiltersPersistenceParams<Row, Ctx>): UseExternalFiltersPersistenceResult => {
  const [externalFiltersSelected, setExternalFiltersSelected] =
    useState<ExternalFilters>({})

  const externalFiltersPersist = config?.persist !== false
  const initializedKeyRef = useRef<string | null>(null)

  const externalFiltersStorageKey = useMemo(() => {
    if (!config) return ''
    return getExternalFiltersStorageKey(config, scope)
  }, [config, scope])

  useEffect(() => {
    if (!config) {
      setExternalFiltersSelected({})
      return
    }
    if (!externalFiltersStorageKey) return
    const initToken = `${externalFiltersStorageKey}:${externalFiltersPersist ? 'persist' : 'no-persist'}`
    if (initializedKeyRef.current === initToken) return
    initializedKeyRef.current = initToken

    // Always initialize state. When persistence is disabled we still want
    // defaultSelected to apply, but we avoid touching storage.
    if (!externalFiltersPersist) {
      const defaults = pruneExternalFilters(config.defaultSelected ?? {}, config.fields)
      setExternalFiltersSelected(defaults)
      return
    }

    const loaded = loadExternalFiltersFromStorage({ config, scope })
    setExternalFiltersSelected(loaded.selected)
  }, [
    config,
    externalFiltersPersist,
    externalFiltersStorageKey,
    scope,
  ])

  useEffect(() => {
    if (!config) return

    // Keep state aligned with the allowlist, even if a screen accidentally
    // sets unknown fields or an older persisted payload leaks in.
    const pruned = pruneExternalFilters(externalFiltersSelected, config.fields)
    if (!areExternalFiltersEqual(pruned, externalFiltersSelected)) {
      setExternalFiltersSelected(pruned)
      return
    }

    if (!externalFiltersPersist) return
    if (!externalFiltersStorageKey) return

    if (Object.keys(pruned).length === 0) {
      removeStorageItem(externalFiltersStorageKey)
      return
    }
    // Always persist the pruned payload (never persist unknown fields).
    const payload = JSON.stringify(pruned)
    const parsed = parseExternalFiltersString(payload)
    if (!parsed) {
      removeStorageItem(externalFiltersStorageKey)
      return
    }
    writeStorageItem(externalFiltersStorageKey, payload)
  }, [
    config,
    externalFiltersPersist,
    externalFiltersSelected,
    externalFiltersStorageKey,
  ])

  const resetExternalFilters = useCallback(() => {
    setExternalFiltersSelected({})
    if (config && externalFiltersPersist) {
      removeStorageItem(externalFiltersStorageKey)
    }
  }, [config, externalFiltersPersist, externalFiltersStorageKey])

  return {
    externalFiltersSelected,
    setExternalFiltersSelected,
    externalFiltersStorageKey,
    resetExternalFilters,
  }
}
